import { SMDataGrid } from "components/SmDataGrid/SMDataGrid";
import { GridPaginationProps, useErrorProps, useGridLoadingOverlay, useGridProps } from "./helpers";
import { SWRResponse } from "swr";
import {
  GridColDef,
  GridColumnsState,
  GridFilterState,
  GridRowClassNameParams,
  GridRowParams,
  GridScrollParams,
  GridSlotsComponentsProps,
  GridSortingState,
  GridValidRowModel,
  UncapitalizedGridProSlotsComponent,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { GridInitialStatePro, GridStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useRef } from "react";
import { ErrorBoundary } from "components/ErrorBoundary";
import { useMobile } from "hooks/Mobile/useMobile";
import { EmptyData } from "components/Table";

export type Props<Model extends GridValidRowModel> = {
  /**
   * Name is used to remember state and scroll position.
   * If not provided then state and scroll position is not remembered.
   */
  name?: string;
  filterName?: string;
  initialState?: GridInitialStatePro | undefined;
  columns: GridColDef<Model>[];
  response: SWRResponse<Model[]>;
  slots?: Partial<UncapitalizedGridProSlotsComponent> | undefined;
  slotProps?: GridSlotsComponentsProps | undefined; // GridSlotsComponentsProps, GridColumnMenuProps, GridColumnMenuSlotProps
  onRowClick?: ((item: GridRowParams<Model & { id: number; orig_id?: any }>) => void) | undefined;
  noRowsMessage?: ReactNode;
  noResultsMessage?: ReactNode;
  disableRememberScroll?: boolean;
  getRowClassName?:
    | ((
        params: GridRowClassNameParams<
          Model & {
            id: number;
            orig_id?: any;
          }
        >
      ) => string)
    | undefined;
  clickableRow?: boolean;
  noRowsOverlay?: JSX.Element;
} & GridPaginationProps;

export const SWRDataGrid = <Model extends GridValidRowModel>(props: Props<Model>) => {
  const {
    name,
    filterName,
    onRowClick,
    response,
    columns,
    initialState,
    pagination,
    paginationModel,
    hideFooter,
    slots,
    slotProps,
    noResultsMessage,
    noRowsMessage,
    disableRememberScroll = false,
    getRowClassName,
    clickableRow = false,
    noRowsOverlay,
  } = props;
  const apiRef = useGridApiRef();
  const gridProps = useGridProps(response);
  const errorProps = useErrorProps(response);
  const rememberStateProps = useRememberState(name, filterName, initialState);
  rememberStateProps.initialState = { ...rememberStateProps.initialState, ...apiRef };

  const { sx, loadingOverlay } = useGridLoadingOverlay(response.isLoading, paginationModel?.pageSize);
  const isMobile = useMobile();

  useRememberScroll(disableRememberScroll ? undefined : name, apiRef);

  const clickableCursorSx = {
    "& .MuiDataGrid-row:hover": {
      cursor: "pointer",
    },
  };

  const baseSx = {
    "& .MuiDataGrid-cellContent": {
      minHeight: 65,
      alignContent: "center",
    },
    "& .MuiDataGrid-columnHeader": {
      minHeight: 65,
      alignContent: "center",
    },
    minHeight: 450,
    "--DataGrid-overlayHeight": "250px",
  };

  const tableSx = clickableRow ? { ...baseSx, ...clickableCursorSx } : baseSx;

  return (
    <ErrorBoundary hasMargin={false} {...errorProps}>
      <SMDataGrid
        apiRef={apiRef}
        {...gridProps}
        {...rememberStateProps}
        sx={{
          ...sx,
          ...tableSx,
        }}
        density="comfortable"
        columns={columns as GridColDef<Model & { id: number; orig_id?: any }>[]}
        rowSelection={false}
        disableDensitySelector
        slots={{
          loadingOverlay,
          noResultsOverlay: () => (
            <EmptyData sx={{ minHeight: "200px" }} message={noResultsMessage ?? "No results found for filters"} />
          ),
          noRowsOverlay: () =>
            noRowsOverlay ?? <EmptyData sx={{ minHeight: "200px" }} message={noRowsMessage ?? "No results found"} />,
          ...slots,
        }}
        slotProps={{
          ...slotProps,
        }}
        onRowClick={onRowClick}
        getRowHeight={() => "auto"}
        {...{
          pagination,
          paginationModel,
          hideFooter,
          getRowClassName,
        }}
        disableColumnPinning={isMobile}
        key={name}
      />
    </ErrorBoundary>
  );
};

export const useRememberState = (
  name: string | undefined,
  filterName: string | undefined,
  defaultInitialState?: GridInitialStatePro | undefined
) => {
  const initialState = useMemo(
    () => (name ? mergeSavedState(name, filterName, defaultInitialState) : defaultInitialState),
    [name, filterName, defaultInitialState]
  );

  const onStateChange = useMemo(() => (name ? gridStateSaver(name, filterName) : undefined), [name, filterName]);
  return {
    initialState,
    onStateChange,
  };
};

export const useRestoreScroll = (name: string, apiRef: React.MutableRefObject<GridApiPro>) => {
  return useCallback(() => {
    const storedScroll = getStoredScroll(name);
    if (apiRef.current && storedScroll) {
      apiRef.current.scroll(storedScroll);
    }
  }, [apiRef, name]);
};

export const useRememberScroll = (name: string | undefined, apiRef: React.MutableRefObject<GridApiPro>) => {
  const hasAttemptedScrollRestore = useRef(false);

  useEffect(() => {
    if (!name) return;
    return apiRef.current.subscribeEvent("scrollPositionChange", (params: GridScrollParams) => {
      if (!hasAttemptedScrollRestore.current) {
        hasAttemptedScrollRestore.current = true;
        const storedScroll = getStoredScroll(name);
        if (apiRef.current && storedScroll) {
          apiRef.current.scroll(storedScroll);
        }
      }
      storeGridScroll(name, params);
    });
  }, [apiRef, name]);
};

export const gridStateSaver = (name: string, filterName: string | undefined) => (state: GridStatePro) => {
  localStorage.setItem(`columns-${name}`, JSON.stringify(state.columns));
  sessionStorage.setItem(`sort-${name}`, JSON.stringify(state.sorting));
  sessionStorage.setItem(`filter-${filterName ?? name}`, JSON.stringify(state.filter));
};

export const storeGridScroll = (name: string, scrollParams: GridScrollParams) => {
  sessionStorage.setItem(`scroll-${name}`, JSON.stringify(scrollParams));
};

export const getStoredScroll = (name: string): GridScrollParams | undefined => {
  return getJsonItem<GridScrollParams>(sessionStorage, `scroll-${name}`);
};

export const mergeSavedState = (
  name: string,
  filterName: string | undefined,
  initialState?: GridInitialStatePro
): GridInitialStatePro => {
  const savedColumns = getJsonItem<GridColumnsState>(localStorage, `columns-${name}`);
  const savedSort = getJsonItem<GridSortingState>(sessionStorage, `sort-${name}`);
  const savedFilter = getJsonItem<GridFilterState>(sessionStorage, `filter-${filterName ?? name}`);

  return {
    columns: { ...initialState?.columns, ...savedColumns },
    sorting: { ...initialState?.sorting, ...(savedSort?.sortModel.length === 0 ? {} : savedSort) },
    filter: { ...initialState?.filter, ...savedFilter },
  };
};

export const getJsonItem = <T,>(storage: Storage, key: string): T | undefined => {
  const rawValue = storage.getItem(key);
  if (!rawValue) return undefined;
  try {
    return JSON.parse(rawValue);
  } catch {
    return undefined;
  }
};
